import Link from "next/link"
import { Phone, Mail, Globe, Linkedin, Facebook, Instagram, Youtube } from "lucide-react"
import Image from "next/image"
import { services } from "@/data/constants"
import { getServiceHref } from "@/lib/utils"
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon"
import { contactInfo } from "@/data/contact"

// Custom X (Twitter) Icon Component
const XIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="26"
    viewBox="0 0 27 26"
    fill="currentColor"
    className={className}
  >
    <path d="M0.651349 0.273926L10.775 14.0793L0.587402 25.3005H2.8815L11.7981 15.4733L19.0042 25.3005H26.8057L16.1146 10.7207L25.5947 0.273926H23.3046L15.0914 9.32262L8.45689 0.273926H0.651349ZM4.02456 1.99399H7.60959L23.4365 23.5764H19.8515L4.02456 1.99399Z"/>
  </svg>
)

export function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-[#030818] text-white py-16 border-t border-gray-800">
      <div className="w-90vw mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
            <Link href="/" className="flex items-center space-x-3">
            
            <Image
              src= "/icons/Groupw.svg"
              alt="ORAPEX"
              width={120}
              height={32}
              className="transition-all duration-300"
            />
          </Link>
            </div>
            <p className="text-gray-400 leading-relaxed max-w-md mb-6">
            Software development done right, securely, and on time.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.linkedin.com/company/orapexoffical/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-3 rounded-lg bg-gray-800 border border-gray-700 hover:bg-gradient-to-r hover:from-[#0077B5] hover:to-[#00A0DC] text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#0077B5]/25"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black border border-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  LinkedIn
                </div>
              </a>
              <a
                href="https://www.facebook.com/ORAPEX/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-3 rounded-lg bg-gray-800 hover:bg-gradient-to-r hover:from-[#1877F2] hover:to-[#42A5F5] text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#1877F2]/25"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black border border-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  Facebook
                </div>
              </a>
              <a
                href="https://www.instagram.com/orapex10/#"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-3 rounded-lg bg-gray-800 hover:bg-gradient-to-r hover:from-[#E4405F] hover:to-[#F77737] text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#E4405F]/25"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black border border-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  Instagram
                </div>
              </a>
              <a
                href="https://www.youtube.com/@ORAPEX_Official"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-3 rounded-lg bg-gray-800 hover:bg-gradient-to-r hover:from-[#FF0000] hover:to-[#FF4444] text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#FF0000]/25"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black border border-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  YouTube
                </div>
              </a>
              <a
                href="https://x.com/ORAPEX_Official"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-3 rounded-lg bg-gray-800 hover:bg-gradient-to-r hover:from-[#000000] hover:to-[#333333] text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#000000]/25"
                aria-label="X"
              >
                <XIcon className="h-5 w-5" />
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black border border-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  X
                </div>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {services.slice(0, 4).map((service, index) => {
                return (
                  <li key={service.id || index}>
                    <Link
                      href={getServiceHref(service)}
                      className="text-gray-400 hover:text-[#00dd98] transition-colors duration-300 block"
                    >
                      {service.title}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-6">
              Contact
            </h4>
            <ul className="space-y-3">
              <li className="text-gray-400">
                <div className="flex flex-col space-y-2">
                  <a 
                    href={contactInfo.whatsapp.ksa.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 hover:text-[#25D366] transition-colors duration-200"
                  >
                    <WhatsAppIcon className="h-4 w-4 " />
                    <span>{contactInfo.whatsapp.ksa.number} ({contactInfo.whatsapp.ksa.region})</span>
                  </a>
                  <a 
                    href={contactInfo.whatsapp.uae.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 hover:text-[#25D366] transition-colors duration-200"
                  >
                    <WhatsAppIcon className="h-4 w-4 " />
                    <span>{contactInfo.whatsapp.uae.number} ({contactInfo.whatsapp.uae.region})</span>
                  </a>
                  <a 
                    href={contactInfo.phone.href}
                    className="flex items-center space-x-2 hover:text-[#00dd98] transition-colors duration-200"
                  >
                    <Phone className="h-4 w-4 " />
                    <span>{contactInfo.phone.number} ({contactInfo.phone.region})</span>
                  </a>
                </div>
              </li>
              <li className="text-gray-400">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>{contactInfo.email}</span>
                </div>
              </li>
              <li className="text-gray-400">
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>www.orapex.com</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {currentYear} ORAPEX. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link
                href="/privacy"
                className="text-[#00dd98]/90 hover:text-[#00dd98] transition-colors duration-300 text-sm hover:underline underline-offset-4 decoration-[#00dd98]/50"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-[#00dd98]/90 hover:text-[#00dd98] transition-colors duration-300 text-sm hover:underline underline-offset-4 decoration-[#00dd98]/50"
              >
                Terms of Service
              </Link>
              <Link
                href="/cookies"
                className="text-[#00dd98]/90 hover:text-[#00dd98] transition-colors duration-300 text-sm hover:underline underline-offset-4 decoration-[#00dd98]/50"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
